// src/utils/printDeliveryWaybill.js
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import logo512 from './logo512.png';
import bus512 from './bus512.png';
import onnibusPng from './onnibus.png';

// ─── Timestamp normalizer ────────────────────────────────────────────────────

function normalizeTs(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatTime(timestamp) {
  const d = normalizeTs(timestamp);
  if (!d) return '—';
  return d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
}

function formatFullDate(timestamp) {
  const d = normalizeTs(timestamp);
  if (!d) return '—';
  return d.toLocaleDateString('fi-FI', {
    weekday: 'short', day: 'numeric', month: 'numeric', year: 'numeric',
  });
}

function formatTimeRange(from, to, hasWindow) {
  if (!from) return '—';
  const datePart = formatFullDate(from);
  const fromPart = formatTime(from);
  if (hasWindow && to) {
    return `${datePart}  ${fromPart} – ${formatTime(to)}`;
  }
  return `${datePart}  ${fromPart}`;
}

// ─── Extract city name from full address ─────────────────────────────────────

function extractCityName(addressString) {
  if (!addressString) return '';
  
  // Try to extract meaningful city name from address
  // Examples: "Loimaa, Suomi" → "Loimaa"
  //          "tampere" → "tampere"
  //          "jalasjärvi neste" → "jalasjärvi neste" (keep as-is, or take first part?)
  
  // Simple approach: take first word(s) before comma, capitalize properly
  const parts = addressString.split(',');
  let cityPart = parts[0].trim();
  
  // Capitalize first letter of each word
  cityPart = cityPart
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  return cityPart;
}

// ─── Main export ─────────────────────────────────────────────────────────────

export const printDeliveryWaybill = async (delivery) => {
  const doc = new jsPDF();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const M = 14;
  const X = M + 4;

  let qrDataUrl;
  try {
    const qrContent = 'www.pakettihuolto.fi/vastaanota?d=' + (delivery.deliveryId || delivery.id || '');
    qrDataUrl = await QRCode.toDataURL(qrContent, {
      width: 200, margin: 1,
      color: { dark: "#000000", light: "#ffffff" },
    });
  } catch (e) { /* ignore */ }

  const idShort = (delivery.deliveryId || delivery.id || 'N/A').slice(-8).toUpperCase();

  let yPos = 0;

  const ensureSpace = (needed) => {
    if (yPos + needed > ph - 45) { doc.addPage(); yPos = 20; }
  };

  const separator = () => {
    doc.setDrawColor(210, 210, 210);
    doc.line(M, yPos, pw - M, yPos);
    yPos += 6;
  };

  // ── Header ───────────────────────────────────────────────────────────────

  yPos = 18;

  // Logo — top left
  try {
    const logoSource = delivery.busTransportData ? bus512 : logo512;
    const logo = await fetch(logoSource).then((res) => res.blob());
    const logoUrl = URL.createObjectURL(logo);
    const img = new Image();
    await new Promise((resolve) => {
      img.onload = () => {
        if (delivery.busTransportData) {
          doc.addImage(img, "PNG", M, 8, 38, 24);
        } else {
          doc.addImage(img, "PNG", M, 8, 24, 24);
        }
        resolve();
      };
      img.onerror = () => resolve();
      img.src = logoUrl;
    });
  } catch (e) {
    console.error('Error loading logo:', e);
  }

  // Title — centered
  const title = delivery.busTransportData
    ? 'Pikapaketti - Rahtikirja'
    : 'Pakettihuolto - Rahtikirja';
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pw / 2, yPos, { align: 'center' });

  // QR code — top right
  if (qrDataUrl) {
    const qrSize = 30;
    const qrX = pw - M - qrSize;
    const qrY = 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Lähetysvarmenne', qrX - 2 + qrSize / 2, qrY - 1, { align: 'center' });
    doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Tunnus: ${idShort}`, qrX + qrSize / 2, qrY + qrSize + 3, { align: 'center' });
  }

  yPos = 46;
  separator();

  // ── Nouto (origin) ───────────────────────────────────────────────────────

  const originInfo      = delivery.originInfo ?? delivery.fromInfo ?? null;
  const destinationInfo = delivery.destinationInfo ?? delivery.toInfo ?? null;
  const hasWindow       = !!delivery.hasTimeWindow;

  ensureSpace(40);
  yPos = printAddressBlock(doc, {
    title:        'NOUTO',
    timeFrom:     delivery.pickupTime   ?? delivery.pickUpTime,
    timeTo:       delivery.latestPickUpTime ?? delivery.latestPickupTime,
    hasWindow,
    address:      originInfo?.address ?? delivery.origin ?? delivery.from,
    city:         originInfo?.city,
    postalCode:   originInfo?.postalCode,
    details:      originInfo?.details,
    contactName:  delivery.senderInfo?.name  || delivery.senderName,
    contactPhone: delivery.senderPhone,
    contactLabel: 'Lähettäjä',
  }, yPos, X, pw, M);

  yPos += 4;

  // ── Toimitus (destination) ───────────────────────────────────────────────

  ensureSpace(40);
  yPos = printAddressBlock(doc, {
    title:        'TOIMITUS',
    timeFrom:     delivery.targetTime,
    timeTo:       delivery.earliestTargetTime,
    hasWindow,
    address:      destinationInfo?.address ?? delivery.destination ?? delivery.to,
    city:         destinationInfo?.city,
    postalCode:   destinationInfo?.postalCode,
    details:      destinationInfo?.details,
    contactName:  delivery.receiverInfo?.name  || delivery.receiverName,
    contactPhone: delivery.receiverPhone,
    contactLabel: 'Vastaanottaja',
  }, yPos, X, pw, M);

  yPos += 4;

  // ── Bus transport ────────────────────────────────────────────────────────

  if (delivery.busTransportData) {
    const bus = delivery.busTransportData;
    const steps = Array.isArray(bus.legs) ? bus.legs : [];

    ensureSpace(25);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('KULJETUSREITTI', X, yPos);
    yPos += 7;

    if (steps.length > 0) {
      const tableX = X;
      const colWidths = { time: 32, from: 40, to: 40, company: 35, line: 25 };

      ensureSpace(10);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(80, 80, 80);

      let colX = tableX;
      doc.text('AIKA',  colX, yPos); colX += colWidths.time;
      doc.text('MISTÄ', colX, yPos); colX += colWidths.from;
      doc.text('MIHIN', colX, yPos); colX += colWidths.to;
      doc.text('YHTIÖ', colX, yPos); colX += colWidths.company;
      doc.text('LINJA', colX, yPos);

      doc.setTextColor(0, 0, 0);
      yPos += 5;

      doc.setDrawColor(200, 200, 200);
      doc.line(tableX, yPos - 1, tableX + colWidths.time + colWidths.from + colWidths.to + colWidths.company + colWidths.line, yPos - 1);
      yPos += 3;

      steps.forEach((step) => {
        ensureSpace(12);

        const from    = step.origin      || '—';
        const to      = step.destination || '—';
        const start   = normalizeTs(step.start);
        const end     = normalizeTs(step.end);
        const company = step.exactName   || step.busCompany || '—';
        const line    = step.shortName   || step.exactLine  || '—';

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        colX = tableX;

        const timeStr = start && end ? `${formatTime(start)}-${formatTime(end)}`
          : start ? formatTime(start) : '—';
        doc.text(timeStr, colX, yPos); colX += colWidths.time;

        const fromText = doc.splitTextToSize(from, colWidths.from - 2);
        doc.text(fromText[0] || '—', colX, yPos); colX += colWidths.from;

        const toText = doc.splitTextToSize(to, colWidths.to - 2);
        doc.text(toText[0] || '—', colX, yPos); colX += colWidths.to;

        const companyText = doc.splitTextToSize(company, colWidths.company - 2);
        doc.text(companyText[0] || '—', colX, yPos); colX += colWidths.company;

        doc.setFont('helvetica', 'bold');
        doc.text(line, colX, yPos);
        doc.setFont('helvetica', 'normal');

        yPos += 6;
      });
    } else if (bus.departure?.station || bus.arrival?.station) {
      ensureSpace(15);
      doc.setFontSize(9);
      if (bus.departure?.station) {
        doc.text(`Lähtö: ${bus.departure.station}  ${formatTime(bus.departure.time)}`, X, yPos);
        yPos += 5;
      }
      if (bus.arrival?.station) {
        doc.text(`Perillä: ${bus.arrival.station}  ${formatTime(bus.arrival.time)}`, X, yPos);
        yPos += 5;
      }
      if (bus.busNumber) {
        doc.text(`Vuoro: ${bus.busNumber}`, X, yPos);
        yPos += 5;
      }
    }

    yPos += 4;
    separator();

    // ── Simplified route summary with times (centered, stacked) ───────

    ensureSpace(20);
    yPos += 2;

    // Build route stops with transfer times
    const routeCities = [];
    
    steps.forEach((step, idx) => {
      const start = normalizeTs(step.start);
      const end = normalizeTs(step.end);
      
      // Add origin (first step only)
      if (idx === 0 && step.origin) {
        const cityName = extractCityName(step.origin);
        routeCities.push({ city: cityName, timeRange: null });
      }

      // Add destination
      if (step.destination?.trim()) {
        const cityName = extractCityName(step.destination);
        
        // Check if this city already exists
        let existingCity = routeCities.find(s => s.city === cityName);
        
        if (!existingCity) {
          existingCity = { city: cityName, timeRange: null };
          routeCities.push(existingCity);
        }
      }
      
      // For wait steps, use origin to find the transfer city and set time range
      if (step.type === 'wait' && step.origin) {
        const cityName = extractCityName(step.origin);
        let existingCity = routeCities.find(s => s.city === cityName);
        
        if (existingCity) {
          const arrivalTime = formatTime(start);
          const departureTime = formatTime(end);
          existingCity.timeRange = `${arrivalTime} - ${departureTime}`;
        }
      }
    });

    if (routeCities.length > 0) {
      // Calculate spacing for columns
      const numStops = routeCities.length;
      const pageWidth = pw - (M * 2);
      const colWidth = pageWidth / numStops;
      const startX = M + (colWidth / 2);

      // City names row
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 60);

      routeCities.forEach((stop, idx) => {
        const x = startX + (idx * colWidth);
        doc.text(stop.city, x, yPos, { align: 'center' });
      });

      yPos += 7;

      // Times row — show transfer ranges only
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);

      routeCities.forEach((stop, idx) => {
        if (stop.timeRange) {
          const x = startX + (idx * colWidth);
          doc.text(stop.timeRange, x, yPos, { align: 'center' });
        }
      });

      doc.setTextColor(0, 0, 0);
      yPos += 5;
    }
  }

  // ── Return method note ───────────────────────────────────────────────────

  if (delivery.returnParcels) {
    ensureSpace(12);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const methodLabel = delivery.returnMethod === 'posti' ? 'Posti' : 'Bussilla';
    doc.text(`Palautuskuljetus: ${methodLabel}`, X, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 7;
  }

  // ── Footer ───────────────────────────────────────────────────────────────

  const isBus   = !!delivery.busTransportData;
  const bannerH = isBus ? 28 : 18;
  const bannerY = ph - bannerH - 6;

  // Top border only — no background fill
  doc.setDrawColor(210, 210, 210);
  doc.line(M, bannerY, pw - M, bannerY);

  if (isBus) {
    const logoH = 9.1;
    const logoW = 28.6;
    const logoX = M + 4;
    const logoY = bannerY + 6;

    try {
      const onnibusImg = new Image();
      await new Promise((resolve) => {
        onnibusImg.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width  = onnibusImg.naturalWidth;
          canvas.height = onnibusImg.naturalHeight;
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(onnibusImg, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          doc.addImage(dataUrl, 'PNG', logoX, logoY, logoW, logoH);
          resolve();
        };
        onnibusImg.onerror = () => resolve();
        onnibusImg.src = onnibusPng;
      });
    } catch (e) { /* ignore if image missing */ }

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(
      'Yhteistyössä Pakettihuolto ja Onnibus.com Oy:n kumppaniliikennöitsijät',
      logoX,
      logoY + logoH + 5,
    );

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(
      'Pakettihuolto  ·  Asiakaspalvelu ma–pe 8–16  ·  010 200 5000',
      pw - M - 4,
      bannerY + 9,
      { align: 'right' },
    );
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text('Päivystys: 010 200 5009  (ma–pe 16:00–20:00)', pw - M - 4, bannerY + 15, { align: 'right' });
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text('© Pakettihuolto 2026', pw - M - 4, bannerY + 21, { align: 'right' });

  } else {
    const midY = bannerY + bannerH / 2;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(
      'Pakettihuolto Oy  ·  Asiakaspalvelu ma–pe 8–16  ·  010 200 5000',
      pw / 2,
      midY - 1,
      { align: 'center' },
    );
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text('© Pakettihuolto Oy 2026', pw / 2, midY + 4, { align: 'center' });
  }

  doc.setTextColor(0, 0, 0);

  doc.save(`rahtikirja_${idShort.toLowerCase()}.pdf`);
};

// ─── Address block helper ────────────────────────────────────────────────────

function printAddressBlock(doc, opts, yPos, X, pw, M) {
  const {
    title, timeFrom, timeTo, hasWindow,
    address, city, postalCode, details,
    contactName, contactPhone,
    contactLabel,                       // 'Lähettäjä' / 'Vastaanottaja'
  } = opts;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(title, X, yPos);

  if (timeFrom) {
    const timeLabel = formatTimeRange(timeFrom, timeTo, hasWindow);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    doc.text(timeLabel, pw - M, yPos, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  }

  yPos += 6;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const addrLines = doc.splitTextToSize(address || '—', 150);
  doc.text(addrLines, X, yPos);
  yPos += addrLines.length * 5;

  const cityLine = [postalCode, city].filter(Boolean).join(' ');
  if (cityLine) {
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text(cityLine, X, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 5;
  }

  if (details) {
    yPos += 1;
    const parts = [];
    if (details.apartment?.trim()) parts.push(`As. ${details.apartment}`);
    if (details.floor?.trim())     parts.push(`${details.floor}. krs`);
    if (details.building?.trim())  parts.push(details.building);

    if (parts.length > 0) {
      doc.setFontSize(9);
      doc.text(parts.join(' · '), X, yPos);
      yPos += 5;
    }

    if (details.accessCode?.trim()) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`Ovikoodi: ${details.accessCode}`, X, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 5;
    }

    if (details.instructions?.trim()) {
      doc.setFontSize(8);
      doc.setTextColor(80, 80, 80);
      const lines = doc.splitTextToSize(details.instructions, 150);
      doc.text(lines, X, yPos);
      yPos += lines.length * 4;
      doc.setTextColor(0, 0, 0);
    }
  }

  // ── Contact — simple labeled line ──────────────────────────────────────
  const hasContact = contactName?.trim() || contactPhone?.trim();
  if (hasContact) {
    yPos += 3;

    const valueParts = [];
    if (contactName?.trim())  valueParts.push(contactName.trim());
    if (contactPhone?.trim()) valueParts.push(`Puh: ${contactPhone.trim()}`);
    const value = valueParts.join('  ·  ');

    doc.setFontSize(9);
    if (contactLabel) {
      doc.setFont('helvetica', 'bold');
      doc.text(`${contactLabel}: `, X, yPos);
      const labelW = doc.getTextWidth(`${contactLabel}: `);
      doc.setFont('helvetica', 'normal');
      doc.text(value, X + labelW, yPos);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.text(value, X, yPos);
    }
    yPos += 5;
  }

  return yPos;
}